import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { declarationsAPI } from '../../services/api';
import Modal from '../../components/Modal';
import RichTextEditor from '../../components/RichTextEditor';

const emptyTemplateForm = {
  name: '',
  code: '',
  description: '',
  location: 'all',
  headerTemplate: '',
  bodyTemplate: '',
  footerTemplate: '',
  customStyles: '',
  fields: []
};

const fieldTypes = ['text', 'date', 'number', 'select'];

const defaultTemplateFields = [
  { key: 'studyName', label: 'Studierichting', type: 'text', required: true, options: [] },
  { key: 'studyYear', label: 'Studiejaar', type: 'text', required: true, options: [] },
  { key: 'studentNumber', label: 'Studentnummer', type: 'text', required: true, options: [] },
  { key: 'dateFrom', label: 'Periode vanaf', type: 'date', required: false, options: [] },
  { key: 'dateTo', label: 'Periode tot', type: 'date', required: false, options: [] }
];

const defaultBodyTemplate = `<p>Hierbij verklaart NLBA dat:</p>
<p><strong>{{student.fullName}}</strong>, geboren op <strong>{{student.dateOfBirth}}</strong>,
ingeschreven staat voor de opleiding <strong>{{data.studyName}}</strong> in studiejaar <strong>{{data.studyYear}}</strong>.</p>
<p>Studentnummer: <strong>{{data.studentNumber}}</strong></p>
<p>Deze verklaring is afgegeven op verzoek van de student.</p>`;

const placeholderHelpText = 'Snelle placeholders: {{student.fullName}}, {{student.dateOfBirth}}, {{request.createdAt}}, {{data.studyName}}, {{data.studyYear}}, {{data.studentNumber}}';

const AdminDeclarations = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('templates');

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm);
  const [templateMode, setTemplateMode] = useState('simple');
  const [newField, setNewField] = useState({ key: '', label: '', type: 'text', required: true, options: '' });

  const [requestSigner, setRequestSigner] = useState({});

  const loadData = async () => {
    const [tpl, req] = await Promise.all([declarationsAPI.getTemplates(), declarationsAPI.getRequests()]);
    setTemplates(tpl.templates || []);
    setRequests(req.requests || []);
  };

  useEffect(() => {
    loadData();
  }, []);

  const pendingCount = useMemo(() => requests.filter((req) => req.status === 'pending').length, [requests]);

  const openCreateTemplateModal = () => {
    setEditingTemplate(null);
    setTemplateMode('simple');
    setTemplateForm({
      ...emptyTemplateForm,
      headerTemplate: '<div style="display:flex;justify-content:space-between;align-items:center;"><h2 style="margin:0;">NLBA Verklaring</h2><span>{{request.createdAt}}</span></div>',
      bodyTemplate: defaultBodyTemplate,
      footerTemplate: '<p style="font-size:12px;">NLBA | Officieel document</p>',
      fields: defaultTemplateFields
    });
    setNewField({ key: '', label: '', type: 'text', required: true, options: '' });
    setShowTemplateModal(true);
  };

  const openEditTemplateModal = (template) => {
    setEditingTemplate(template);
    setTemplateMode('advanced');
    setTemplateForm({
      name: template.name || '',
      code: template.code || '',
      description: template.description || '',
      location: template.location || 'all',
      headerTemplate: template.headerTemplate || '',
      bodyTemplate: template.bodyTemplate || '',
      footerTemplate: template.footerTemplate || '',
      customStyles: template.customStyles || '',
      fields: Array.isArray(template.fields) ? template.fields : []
    });
    setNewField({ key: '', label: '', type: 'text', required: true, options: '' });
    setShowTemplateModal(true);
  };

  const addField = () => {
    const key = newField.key.trim();
    const label = (newField.label || '').trim() || key;
    if (!key) return;

    if (templateForm.fields.some((field) => field.key === key)) {
      window.alert('Dit veld bestaat al in deze template.');
      return;
    }

    const options = newField.type === 'select'
      ? (newField.options || '').split(',').map((item) => item.trim()).filter(Boolean)
      : [];

    setTemplateForm((prev) => ({
      ...prev,
      fields: [...prev.fields, { key, label, type: newField.type, required: !!newField.required, options }]
    }));

    setNewField({ key: '', label: '', type: 'text', required: true, options: '' });
  };

  const removeField = (key) => {
    setTemplateForm((prev) => ({ ...prev, fields: prev.fields.filter((field) => field.key !== key) }));
  };

  const updateField = (key, updates) => {
    setTemplateForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((field) => (field.key === key ? { ...field, ...updates } : field))
    }));
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();

    const payload = {
      ...templateForm,
      code: (templateForm.code || '').toUpperCase().trim()
    };

    if (editingTemplate?.id) {
      await declarationsAPI.updateTemplate(editingTemplate.id, payload);
    } else {
      await declarationsAPI.createTemplate(payload);
    }

    setShowTemplateModal(false);
    setEditingTemplate(null);
    setTemplateForm(emptyTemplateForm);
    await loadData();
  };

  const handleReview = async (requestId, status) => {
    const signedByName = requestSigner[requestId] || 'NLBA Staff';

    await declarationsAPI.reviewRequest(requestId, {
      status,
      signedByName,
      signatureData: 'digital-signature-placeholder'
    });

    await loadData();
  };

  const handleViewDocument = async (requestId) => {
    const blob = await declarationsAPI.getDocumentBlob(requestId, 'view');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleDownloadDocument = async (requestId) => {
    const blob = await declarationsAPI.getDocumentBlob(requestId, 'download');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `declaration_${requestId}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>{t('declaration.title')}</h1>
        {activeTab === 'templates' && (
          <button className="btn-primary" onClick={openCreateTemplateModal}>{t('common.add')}</button>
        )}
      </div>

      <div className="form-row" style={{ marginBottom: '16px' }}>
        <button
          type="button"
          className={activeTab === 'templates' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setActiveTab('templates')}
        >
          Templates
        </button>
        <button
          type="button"
          className={activeTab === 'requests' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setActiveTab('requests')}
        >
          Aanvragen ({pendingCount} open)
        </button>
      </div>

      {activeTab === 'templates' && (
        <>
          <h2>{t('declaration.template')}</h2>
          <div className="user-grid">
            {templates.map((tpl) => (
              <div key={tpl.id} className="user-card" style={{ display: 'block' }}>
                <h3>{tpl.name} ({tpl.code})</h3>
                <p>{tpl.description || '-'}</p>
                <p><strong>Locatie:</strong> {tpl.location}</p>
                <p><strong>Velden:</strong> {(tpl.fields || []).map((field) => field.key).join(', ') || '-'}</p>
                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => openEditTemplateModal(tpl)}>Bewerk</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'requests' && (
        <>
          <h2>{t('declaration.request')}</h2>
          <div className="user-grid">
            {requests.map((req) => (
              <div key={req.id} className="user-card" style={{ display: 'block' }}>
                <h3>{req.template?.name}</h3>
                <p><strong>Student:</strong> {req.student?.name}</p>
                <p><strong>Status:</strong> {req.status}</p>
                {req.generatedDocument && (
                  <div className="form-actions" style={{ marginBottom: '10px' }}>
                    <button type="button" className="btn-secondary" onClick={() => handleViewDocument(req.id)}>View</button>
                    <button type="button" className="btn-secondary" onClick={() => handleDownloadDocument(req.id)}>Download</button>
                  </div>
                )}

                {req.status === 'pending' && (
                  <>
                    <input
                      className="form-input"
                      value={requestSigner[req.id] || ''}
                      onChange={(e) => setRequestSigner((prev) => ({ ...prev, [req.id]: e.target.value }))}
                      placeholder="Ondertekenaar (optioneel)"
                    />
                    <div className="form-actions" style={{ marginTop: '8px' }}>
                      <button className="btn-primary" onClick={() => handleReview(req.id, 'approved')}>Approve</button>
                      <button className="btn-delete" onClick={() => handleReview(req.id, 'rejected')}>Reject</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title={editingTemplate ? 'Template bewerken' : 'Template aanmaken'}
      >
        <form onSubmit={handleSaveTemplate} className="add-form">
          <div className="form-row" style={{ marginBottom: '6px' }}>
            <button
              type="button"
              className={templateMode === 'simple' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setTemplateMode('simple')}
            >
              Simpel
            </button>
            <button
              type="button"
              className={templateMode === 'advanced' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setTemplateMode('advanced')}
            >
              Geavanceerd
            </button>
          </div>
          <small style={{ color: '#4b5563' }}>
            Simpel gebruikt standaardvelden en basisopmaak. Geavanceerd geeft volledige vrijheid.
          </small>

          <div className="form-row">
            <input
              className="form-input"
              value={templateForm.name}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t('common.name')}
              required
            />
            <input
              className="form-input"
              value={templateForm.code}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, code: e.target.value }))}
              placeholder="Code"
              required
              disabled={!!editingTemplate}
            />
          </div>

          <div className="form-row">
            <input
              className="form-input"
              value={templateForm.location}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, location: e.target.value }))}
              placeholder="Locatie"
              list="declaration-template-location-options"
            />
            <textarea
              className="form-textarea"
              value={templateForm.description}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={t('course.description')}
            />
          </div>

          <datalist id="declaration-template-location-options">
            <option value="all" />
            <option value="Paramaribo" />
            <option value="Nickerie" />
          </datalist>

          <div className="form-group">
            <label><strong>Dynamische velden voor aanvragen</strong></label>
            <small style={{ color: '#4b5563', display: 'block', marginBottom: '8px' }}>
              Deze standaardvelden zijn al ingevuld. Je kunt labels aanpassen en velden verplicht maken.
            </small>

            <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(templateForm.fields || []).map((field) => (
                <div key={field.key} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 0.8fr 0.8fr auto', gap: '8px', alignItems: 'center' }}>
                  <input
                    className="form-input"
                    value={field.key}
                    disabled
                  />
                  <input
                    className="form-input"
                    value={field.label || ''}
                    onChange={(e) => updateField(field.key, { label: e.target.value })}
                  />
                  <select
                    className="form-input"
                    value={field.type}
                    onChange={(e) => updateField(field.key, { type: e.target.value })}
                  >
                    {fieldTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={!!field.required}
                      onChange={(e) => updateField(field.key, { required: e.target.checked })}
                    />
                    Verplicht
                  </label>
                  <button type="button" className="btn-delete" onClick={() => removeField(field.key)}>Verwijder</button>
                </div>
              ))}
            </div>

            {templateMode === 'advanced' && (
              <>
            <div className="form-row">
              <input
                className="form-input"
                value={newField.key}
                onChange={(e) => setNewField((prev) => ({ ...prev, key: e.target.value }))}
                placeholder="Veld key (bijv. studyYear)"
              />
              <input
                className="form-input"
                value={newField.label}
                onChange={(e) => setNewField((prev) => ({ ...prev, label: e.target.value }))}
                placeholder="Label"
              />
            </div>
            <div className="form-row">
              <select
                className="form-input"
                value={newField.type}
                onChange={(e) => setNewField((prev) => ({ ...prev, type: e.target.value }))}
              >
                {fieldTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={!!newField.required}
                  onChange={(e) => setNewField((prev) => ({ ...prev, required: e.target.checked }))}
                />
                Verplicht
              </label>

              <button type="button" className="btn-secondary" onClick={addField}>Veld toevoegen</button>
            </div>
            {newField.type === 'select' && (
              <input
                className="form-input"
                value={newField.options}
                onChange={(e) => setNewField((prev) => ({ ...prev, options: e.target.value }))}
                placeholder="Opties (komma gescheiden)"
              />
            )}
              </>
            )}
          </div>

          {templateMode === 'advanced' && (
            <RichTextEditor
              label="Header (Word-stijl, incl. afbeeldingen)"
              value={templateForm.headerTemplate}
              onChange={(value) => setTemplateForm((prev) => ({ ...prev, headerTemplate: value }))}
              placeholder="Gebruik placeholders zoals {{student.fullName}} en {{request.createdAt}}"
              minHeight={140}
            />
          )}

          <RichTextEditor
            label="Body template"
            value={templateForm.bodyTemplate}
            onChange={(value) => setTemplateForm((prev) => ({ ...prev, bodyTemplate: value }))}
            placeholder="Gebruik placeholders zoals {{student.fullName}}, {{data.studyName}}, {{data.studyYear}}"
            minHeight={240}
          />
          <small style={{ color: '#4b5563' }}>{placeholderHelpText}</small>

          {templateMode === 'advanced' && (
            <>
              <RichTextEditor
                label="Footer"
                value={templateForm.footerTemplate}
                onChange={(value) => setTemplateForm((prev) => ({ ...prev, footerTemplate: value }))}
                placeholder="Bijv. contactinformatie of standaard slottekst"
                minHeight={120}
              />

              <textarea
                className="form-textarea"
                value={templateForm.customStyles}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, customStyles: e.target.value }))}
                placeholder="Extra CSS (optioneel), bv .content h1 { color: #0f172a; }"
                style={{ minHeight: '100px', fontFamily: 'monospace' }}
              />
            </>
          )}

          <div className="form-actions">
            <button className="btn-primary" type="submit">{t('common.save')}</button>
            <button className="btn-secondary" type="button" onClick={() => setShowTemplateModal(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminDeclarations;
